/**
 * ResourceScopedPermissionBuilder Component
 *
 * Visual builder for creating and editing ResourceScopedPermission arrays.
 * Works directly with the new format: { actions: string[], resources: string[], name?: string, description?: string }
 *
 * Features:
 * - Permission group cards with editable names and descriptions
 * - Smart resource builder with guided resource selection
 * - Domain-grouped action selection (app, data, iam)
 * - Support for both tenant and application role permissions
 */

import React, { useState, useMemo } from 'react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Spinner';
import { useApplications } from '../../applications/hooks/useApplications';
import {
  useResourceOptions,
  RESOURCE_TYPE_CONFIGS,
  getResourcePatternLabel,
  parseResourcePattern,
  buildResourcePattern,
  type ResourceType,
  type ResourceOption,
} from '../hooks/useResourceOptions';
import type { ResourceScopedPermission } from '../../../types/role.types';
import type { ApplicationSanitized } from '../../../types/application.types';
import {
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  ServerStackIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

/**
 * Available permission actions grouped by domain
 */
const PERMISSION_ACTIONS = {
  application: {
    label: 'Application',
    icon: ServerStackIcon,
    actions: [
      { action: 'application:InvokeFunction', label: 'Execute Functions', description: 'Execute application functions' },
      { action: 'application:InvokeJob', label: 'Invoke Jobs', description: 'Create background jobs' },
    ],
  },
  data: {
    label: 'Data',
    icon: ServerStackIcon,
    actions: [
      { action: 'volume:GetObject', label: 'Read Volumes', description: 'Read objects from volumes' },
      { action: 'volume:PutObject', label: 'Write Volumes', description: 'Write objects to volumes' },
      { action: 'volume:DeleteObject', label: 'Delete from Volumes', description: 'Delete objects from volumes' },
      { action: 'volume:ListObjects', label: 'List Volumes', description: 'List objects in volumes' },
      { action: 'secret:GetSecretValue', label: 'Get Secrets', description: 'Read secret values' },
      { action: 'connection:Connect', label: 'Connect to DB', description: 'Connect to database connections' },
      { action: 'connection:Query', label: 'Query Connections', description: 'Execute database queries' },
    ],
  },
  documentdb: {
    label: 'Document Database',
    icon: ServerStackIcon,
    actions: [
      { action: 'documentdb:Connect', label: 'Connect', description: 'Connect to document database' },
      { action: 'documentdb:GetDocument', label: 'Get Document', description: 'Read documents from document database' },
      { action: 'documentdb:PutDocument', label: 'Put Document', description: 'Write documents to document database' },
      { action: 'documentdb:DeleteDocument', label: 'Delete Document', description: 'Delete documents from document database' },
      { action: 'documentdb:Query', label: 'Query', description: 'List and query documents in document database' },
    ],
  },
  ai: {
    label: 'AI',
    icon: ServerStackIcon,
    actions: [
      { action: 'ai:InvokeModel', label: 'Invoke AI Models', description: 'Invoke AI models for chat completions' },
    ],
  },
  iam: {
    label: 'IAM',
    icon: ShieldCheckIcon,
    actions: [
      { action: 'iam:GetUserData', label: 'Read Users', description: 'Access tenant user information' },
      { action: 'iam:GetGroupData', label: 'Read Groups', description: 'Access tenant group information' },
      { action: 'iam:GetMembershipData', label: 'Read Group Members', description: 'Access group membership information' },
      { action: 'iam:ListUsers', label: 'List Users', description: 'List all tenant users' },
    ],
  },
};

/**
 * Tenant permission actions for tenant roles
 */
const TENANT_PERMISSION_DOMAINS = {
  tenant: {
    label: 'Tenant',
    actions: [
      { action: 'tenant:settings:read', label: 'Read Settings' },
      { action: 'tenant:settings:update', label: 'Update Settings' },
      { action: 'tenant:billing:read', label: 'Read Billing' },
      { action: 'tenant:billing:update', label: 'Update Billing' },
    ],
  },
  iam: {
    label: 'IAM',
    actions: [
      { action: 'iam:user:read', label: 'Read Users' },
      { action: 'iam:user:create', label: 'Create Users' },
      { action: 'iam:user:update', label: 'Update Users' },
      { action: 'iam:user:delete', label: 'Delete Users' },
      { action: 'iam:group:read', label: 'Read Groups' },
      { action: 'iam:group:create', label: 'Create Groups' },
      { action: 'iam:group:update', label: 'Update Groups' },
      { action: 'iam:group:delete', label: 'Delete Groups' },
      { action: 'iam:role:read', label: 'Read Roles' },
      { action: 'iam:role:create', label: 'Create Roles' },
      { action: 'iam:role:update', label: 'Update Roles' },
      { action: 'iam:role:delete', label: 'Delete Roles' },
    ],
  },
  app: {
    label: 'Applications',
    actions: [
      { action: 'app:application:read', label: 'Read Applications' },
      { action: 'app:application:create', label: 'Create Applications' },
      { action: 'app:application:update', label: 'Update Applications' },
      { action: 'app:application:delete', label: 'Delete Applications' },
      { action: 'app:deployment:read', label: 'Read Deployments' },
      { action: 'app:deployment:create', label: 'Create Deployments' },
    ],
  },
  data: {
    label: 'Data',
    actions: [
      { action: 'data:volume:read', label: 'Read Volumes' },
      { action: 'data:volume:create', label: 'Create Volumes' },
      { action: 'data:volume:update', label: 'Update Volumes' },
      { action: 'data:volume:delete', label: 'Delete Volumes' },
      { action: 'data:connection:read', label: 'Read Connections' },
      { action: 'data:connection:create', label: 'Create Connections' },
      { action: 'data:secret:read', label: 'Read Secrets' },
      { action: 'data:secret:create', label: 'Create Secrets' },
    ],
  },
};

/**
 * App domain permission actions
 * These are passed to tenant applications as information - enforcement is app-side.
 * Actions are simple names (e.g., "read", "write", "export") - no prefix needed.
 * Resources are just application codes (e.g., "inventory", "sales").
 * They are stored flattened as "applicationCode:action" (e.g., "inventory:read") for data plane compatibility.
 * NOTE: For app domain roles, actions are dynamically loaded from the selected application's actions attribute
 */

/**
 * Maps each action to the resource types it applies to.
 * Used to filter available actions based on selected resources for application roles.
 */
const ACTION_TO_RESOURCE_TYPE: Record<string, ResourceType[]> = {
  'application:InvokeFunction': ['application'],
  'application:InvokeJob': ['application'],
  'volume:GetObject': ['volume'],
  'volume:PutObject': ['volume'],
  'volume:DeleteObject': ['volume'],
  'volume:ListObjects': ['volume'],
  'secret:GetSecretValue': ['secret'],
  'connection:Connect': ['connection', 'managed-db'],
  'connection:Query': ['connection', 'managed-db'],
  'documentdb:Connect': ['documentdb'],
  'documentdb:GetDocument': ['documentdb'],
  'documentdb:PutDocument': ['documentdb'],
  'documentdb:DeleteDocument': ['documentdb'],
  'documentdb:Query': ['documentdb'],
  'iam:GetUserData': ['all'],
  'iam:GetGroupData': ['all'],
  'iam:GetMembershipData': ['all'],
  'iam:ListUsers': ['all'],
};

type SupportedRoleType = 'tenant' | 'application' | 'app_domain';

interface ResourceScopedPermissionBuilderProps {
  tenantId: string;
  permissions: ResourceScopedPermission[];
  onChange: (permissions: ResourceScopedPermission[]) => void;
  roleType?: SupportedRoleType;
  disabled?: boolean;
}

interface PermissionGroupEditorProps {
  index: number;
  permission: ResourceScopedPermission;
  onUpdate: (index: number, permission: ResourceScopedPermission) => void;
  onRemove: (index: number) => void;
  roleType: SupportedRoleType;
  tenantId: string;
  applications?: ApplicationSanitized[];
  disabled?: boolean;
}

/**
 * App Domain Permission Group Editor
 * For app_domain roles: user selects an application first, then chooses from that app's actions
 */
interface AppDomainPermissionGroupEditorProps {
  index: number;
  permission: ResourceScopedPermission;
  onUpdate: (index: number, permission: ResourceScopedPermission) => void;
  onRemove: (index: number) => void;
  applications: ApplicationSanitized[];
  disabled?: boolean;
}

/**
 * Extract application code from app domain resource pattern
 * Handles both old format ("app:inventory") and new format ("inventory")
 */
const extractAppCodeFromResource = (resource: string): string | null => {
  // Handle legacy "app:" prefix format
  if (resource.startsWith('app:')) {
    return resource.slice(4);
  }
  // New format: resource is just the application code
  if (resource && resource !== '*') {
    return resource;
  }
  return null;
};

/**
 * App Domain Permission Group Editor Component
 * Specialized editor for app domain roles where:
 * - User selects ONE application per permission group
 * - Actions come from the application's actions attribute
 * - Resource is automatically set to "app:{appCode}"
 */
const AppDomainPermissionGroupEditor: React.FC<AppDomainPermissionGroupEditorProps> = ({
  index,
  permission,
  onUpdate,
  onRemove,
  applications,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showDescription, setShowDescription] = useState(!!permission.description);

  // Get the selected application code from the resource (first one that starts with "app:")
  const selectedAppCode = useMemo(() => {
    for (const resource of permission.resources) {
      const appCode = extractAppCodeFromResource(resource);
      if (appCode && appCode !== '*') {
        return appCode;
      }
    }
    return '';
  }, [permission.resources]);

  // Get the selected application entity
  const selectedApp = useMemo(() => {
    return applications.find((app) => app.applicationCode === selectedAppCode);
  }, [applications, selectedAppCode]);

  // Get available actions from the selected application
  const availableActions = useMemo(() => {
    if (!selectedApp || !selectedApp.actions) {
      return [];
    }
    // Use simple action names (no prefix)
    return selectedApp.actions.map((action) => ({
      action: action,
      label: action.charAt(0).toUpperCase() + action.slice(1),
    }));
  }, [selectedApp]);

  // Get display name for the permission group
  const displayName = permission.name || (selectedApp ? selectedApp.applicationName : `Permission Group ${index + 1}`);

  // Update name
  const updateName = (name: string) => {
    onUpdate(index, { ...permission, name: name || undefined });
  };

  // Update description
  const updateDescription = (description: string) => {
    onUpdate(index, { ...permission, description: description || undefined });
  };

  // Handle application selection change
  const handleAppChange = (appCode: string) => {
    const app = applications.find((a) => a.applicationCode === appCode);
    if (!app) {
      // Clear selection
      onUpdate(index, {
        ...permission,
        actions: [],
        resources: [],
        name: undefined,
      });
      return;
    }

    // Set the resource with "app:" prefix
    onUpdate(index, {
      ...permission,
      actions: [],
      resources: [`app:${appCode}`],
      name: app.applicationName,
    });
  };

  // Toggle action in the permission group
  const toggleAction = (action: string) => {
    // Extract appCode from resource (which has "app:" prefix)
    const appCode = selectedAppCode;
    if (!appCode) return;

    // Create prefixed action format: "{appCode}:{action}"
    const prefixedAction = `${appCode}:${action}`;

    const newActions = permission.actions.includes(prefixedAction)
      ? permission.actions.filter((a) => a !== prefixedAction)
      : [...permission.actions, prefixedAction];
    onUpdate(index, { ...permission, actions: newActions });
  };

  // Select all actions
  const selectAllActions = () => {
    // Extract appCode from resource
    const appCode = selectedAppCode;
    if (!appCode) return;

    // Prefix all actions with "{appCode}:"
    const allActions = availableActions.map((a) => `${appCode}:${a.action}`);
    onUpdate(index, { ...permission, actions: allActions });
  };

  // Clear all actions
  const clearAllActions = () => {
    onUpdate(index, { ...permission, actions: [] });
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-purple-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
          )}

          {/* Editable Name */}
          {isEditingName && !disabled ? (
            <input
              type="text"
              value={permission.name || ''}
              onChange={(e) => updateName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingName(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder={selectedApp?.applicationName || `Permission Group ${index + 1}`}
              className="flex-1 min-w-0 text-sm font-medium text-gray-700 border-b border-purple-500 bg-transparent focus:outline-none"
              autoFocus
            />
          ) : (
            <span
              className="text-sm font-medium text-gray-700 truncate cursor-text"
              onClick={(e) => {
                if (!disabled) {
                  e.stopPropagation();
                  setIsEditingName(true);
                }
              }}
            >
              {displayName}
            </span>
          )}

          {!disabled && !isEditingName && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingName(true);
              }}
              className="text-gray-400 hover:text-purple-500"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
          )}

          {selectedAppCode && (
            <Badge variant="info" className="text-xs flex-shrink-0">
              app:{selectedAppCode}
            </Badge>
          )}
          <Badge variant="success" className="text-xs flex-shrink-0">
            {permission.actions.length} actions
          </Badge>
        </div>

        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className="text-gray-400 hover:text-red-500 ml-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Application Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedAppCode}
              onChange={(e) => handleAppChange(e.target.value)}
              disabled={disabled}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
            >
              <option value="">Select an application...</option>
              {applications.map((app) => (
                <option key={app.applicationId} value={app.applicationCode}>
                  {app.applicationName} ({app.applicationCode})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select the application this permission group applies to. Resource will be set to{' '}
              <code className="bg-gray-200 px-1 rounded">app:{selectedAppCode || '{appCode}'}</code>
            </p>
          </div>

          {/* Description (optional) */}
          <div>
            {showDescription ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={permission.description || ''}
                  onChange={(e) => updateDescription(e.target.value)}
                  placeholder="Optional description for this permission group..."
                  disabled={disabled}
                  rows={2}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                />
              </div>
            ) : (
              !disabled && (
                <button
                  type="button"
                  onClick={() => setShowDescription(true)}
                  className="text-sm text-purple-600 hover:text-purple-500"
                >
                  + Add description
                </button>
              )
            )}
          </div>

          {/* Actions - only show if application is selected */}
          {selectedAppCode && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Actions <span className="text-red-500">*</span>
                </label>
                {!disabled && availableActions.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllActions}
                      className="text-xs text-purple-600 hover:text-purple-500"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={clearAllActions}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              {availableActions.length > 0 ? (
                <div className="space-y-2">
                  {/* Selected actions */}
                  {permission.actions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {permission.actions.map((action) => {
                        // Extract action name from prefixed format (e.g., "my-app:read" -> "read")
                        const actionName = action.includes(':') ? action.split(':').slice(1).join(':') : action;
                        return (
                          <Badge
                            key={action}
                            variant="success"
                            className="flex items-center gap-1"
                          >
                            <span className="font-mono text-xs">{action}</span>
                            {!disabled && (
                              <button
                                type="button"
                                onClick={() => toggleAction(actionName)}
                                className="ml-1 hover:text-red-600"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  {/* Action checkboxes */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {availableActions.map((actionDef) => {
                      // Check if prefixed action exists in permissions
                      const prefixedAction = selectedAppCode ? `${selectedAppCode}:${actionDef.action}` : actionDef.action;
                      const isChecked = permission.actions.includes(prefixedAction);

                      return (
                        <label
                          key={actionDef.action}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            isChecked
                              ? 'bg-purple-100 border border-purple-300'
                              : 'hover:bg-gray-100 border border-transparent'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleAction(actionDef.action)}
                            disabled={disabled}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">{prefixedAction}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  This application has no defined actions. Add actions to the application first.
                </p>
              )}
            </div>
          )}

          {/* Resource display (read-only for app domain) */}
          {selectedAppCode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resource
              </label>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Badge variant="info" className="font-mono text-xs">
                  app:{selectedAppCode}
                </Badge>
                <p className="mt-2 text-xs text-gray-500">
                  For App Domain roles, the resource is automatically set based on the selected application.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Smart Resource Builder component
 * Provides guided resource selection with dropdowns
 */
interface SmartResourceBuilderProps {
  tenantId: string;
  onAddResource: (resource: string) => void;
  existingResources: string[];
  roleType: SupportedRoleType;
  disabled?: boolean;
}

const SmartResourceBuilder: React.FC<SmartResourceBuilderProps> = ({
  tenantId,
  onAddResource,
  existingResources,
  roleType,
  disabled = false,
}) => {
  const [selectedType, setSelectedType] = useState<ResourceType | ''>('');
  const [selectedResource, setSelectedResource] = useState('');
  const [customPattern, setCustomPattern] = useState('');

  const resourceOptions = useResourceOptions(tenantId);

  // Static AI model options
  const aiModelOptions: ResourceOption[] = [
    { code: 'haiku', name: 'Claude Haiku 4.5' },
    { code: 'sonnet', name: 'Claude Sonnet 4.6' },
    { code: 'sonnet-4.5', name: 'Claude Sonnet 4.5' },
    { code: 'opus', name: 'Claude Opus 4.6' },
  ];

  // Get options for the selected resource type
  const getOptionsForType = (type: ResourceType): ResourceOption[] => {
    switch (type) {
      case 'application':
        return resourceOptions.applications;
      case 'volume':
        return resourceOptions.volumes;
      case 'secret':
        return resourceOptions.secrets;
      case 'connection':
        return resourceOptions.connections;
      case 'documentdb':
        return resourceOptions.documentDbs;
      case 'managed-db':
        return resourceOptions.managedDbs;
      case 'ai':
        return aiModelOptions;
      default:
        return [];
    }
  };

  // Handle adding the resource
  const handleAddResource = () => {
    if (!selectedType) return;

    let resourcePattern = '';

    if (selectedType === 'all') {
      resourcePattern = '*';
    } else if (selectedType === 'custom') {
      resourcePattern = customPattern.trim();
    } else if (selectedResource) {
      resourcePattern = buildResourcePattern(selectedType, selectedResource);
    }

    if (resourcePattern && !existingResources.includes(resourcePattern)) {
      onAddResource(resourcePattern);
      setSelectedType('');
      setSelectedResource('');
      setCustomPattern('');
    }
  };

  // Filter resource types based on role type
  const availableTypes = useMemo(() => {
    // For tenant roles, use different resource types (application instead of app)
    if (roleType === 'tenant') {
      return RESOURCE_TYPE_CONFIGS.filter(
        (c) => c.type === 'all' || c.type === 'application' || c.type === 'custom'
      );
    }
    // For app domain roles, resources are applications only (app:{appCode} format)
    if (roleType === 'app_domain') {
      return RESOURCE_TYPE_CONFIGS.filter(
        (c) => c.type === 'all' || c.type === 'application' || c.type === 'custom'
      );
    }
    return RESOURCE_TYPE_CONFIGS;
  }, [roleType]);

  const options = selectedType && selectedType !== 'all' && selectedType !== 'custom'
    ? getOptionsForType(selectedType)
    : [];

  return (
    <div className="flex flex-col sm:flex-row gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
      {/* Resource Type Selector */}
      <div className="flex-1 min-w-0">
        <select
          value={selectedType}
          onChange={(e) => {
            setSelectedType(e.target.value as ResourceType);
            setSelectedResource('');
            setCustomPattern('');
          }}
          disabled={disabled}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="">Select resource type...</option>
          {availableTypes.map((config) => (
            <option key={config.type} value={config.type}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* Resource Selector (for types that have options) */}
      {selectedType && selectedType !== 'all' && selectedType !== 'custom' && (
        <div className="flex-1 min-w-0">
          <select
            value={selectedResource}
            onChange={(e) => setSelectedResource(e.target.value)}
            disabled={disabled || options.length === 0}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">
              {options.length === 0 ? 'No resources available' : 'Select resource...'}
            </option>
            <option value="*">All (wildcard)</option>
            {options.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.name} ({opt.code})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Custom Pattern Input */}
      {selectedType === 'custom' && (
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={customPattern}
            onChange={(e) => setCustomPattern(e.target.value)}
            placeholder="e.g., volume:exports-*"
            disabled={disabled}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
      )}

      {/* Add Button */}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={handleAddResource}
        disabled={
          disabled ||
          !selectedType ||
          (selectedType !== 'all' && selectedType !== 'custom' && !selectedResource) ||
          (selectedType === 'custom' && !customPattern.trim())
        }
      >
        <PlusIcon className="h-4 w-4 mr-1" />
        Add
      </Button>
    </div>
  );
};

/**
 * Get icon for resource type
 */
const getResourceIcon = (pattern: string): string => {
  const parsed = parseResourcePattern(pattern);
  if (!parsed) return '';
  const config = RESOURCE_TYPE_CONFIGS.find((c) => c.type === parsed.type);
  return config?.icon || '';
};

/**
 * Get badge variant for resource type
 */
const getResourceBadgeVariant = (pattern: string): 'default' | 'warning' | 'info' | 'success' => {
  if (pattern === '*') return 'warning';
  const parsed = parseResourcePattern(pattern);
  if (!parsed) return 'default';
  switch (parsed.type) {
    case 'application':
      return 'info';
    case 'volume':
    case 'secret':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * Single permission group editor
 */
const PermissionGroupEditor: React.FC<PermissionGroupEditorProps> = ({
  index,
  permission,
  onUpdate,
  onRemove,
  roleType,
  tenantId,
  applications = [],
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showDescription, setShowDescription] = useState(!!permission.description);

  // Get display name for the permission group
  const displayName = permission.name || `Permission Group ${index + 1}`;

  // Update name
  const updateName = (name: string) => {
    onUpdate(index, { ...permission, name: name || undefined });
  };

  // Update description
  const updateDescription = (description: string) => {
    onUpdate(index, { ...permission, description: description || undefined });
  };

  // Toggle action in the permission group
  const toggleAction = (action: string) => {
    const newActions = permission.actions.includes(action)
      ? permission.actions.filter((a) => a !== action)
      : [...permission.actions, action];
    onUpdate(index, { ...permission, actions: newActions });
  };

  // Add a resource pattern
  const addResource = (resource: string) => {
    if (permission.resources.includes(resource)) return;
    onUpdate(index, {
      ...permission,
      resources: [...permission.resources, resource],
    });
  };

  // Remove a resource pattern
  const removeResource = (resource: string) => {
    onUpdate(index, {
      ...permission,
      resources: permission.resources.filter((r) => r !== resource),
    });
  };

  // Format action for display
  const formatAction = (action: string) => {
    const parts = action.split(':');
    if (parts.length === 3) {
      return `${parts[1]}:${parts[2]}`;
    }
    return action;
  };

  // Get available actions based on role type
  // Note: app_domain uses AppDomainPermissionGroupEditor, not this component
  const availableActions = useMemo(() => {
    const allActions = roleType === 'application'
      ? Object.values(PERMISSION_ACTIONS).flatMap((domain) => domain.actions)
      : Object.values(TENANT_PERMISSION_DOMAINS).flatMap((domain) => domain.actions);

    // For application roles, filter actions based on selected resource types
    if (roleType === 'application' && permission.resources.length > 0) {
      const selectedResourceTypes = new Set<string>();
      for (const resource of permission.resources) {
        if (resource === '*') {
          // Wildcard means all actions are available
          return allActions;
        }
        const parsed = parseResourcePattern(resource);
        if (parsed) {
          selectedResourceTypes.add(parsed.type);
        }
      }

      if (selectedResourceTypes.size > 0) {
        return allActions.filter((a) => {
          const actionResourceTypes = ACTION_TO_RESOURCE_TYPE[a.action];
          if (!actionResourceTypes) return true;
          return actionResourceTypes.some((t) => selectedResourceTypes.has(t));
        });
      }
    }

    return allActions;
  }, [roleType, permission.resources]);

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}

          {/* Editable Name */}
          {isEditingName && !disabled ? (
            <input
              type="text"
              value={permission.name || ''}
              onChange={(e) => updateName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingName(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder={`Permission Group ${index + 1}`}
              className="flex-1 min-w-0 text-sm font-medium text-gray-700 border-b border-primary-500 bg-transparent focus:outline-none"
              autoFocus
            />
          ) : (
            <span
              className="text-sm font-medium text-gray-700 truncate cursor-text"
              onClick={(e) => {
                if (!disabled) {
                  e.stopPropagation();
                  setIsEditingName(true);
                }
              }}
            >
              {displayName}
            </span>
          )}

          {!disabled && !isEditingName && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingName(true);
              }}
              className="text-gray-400 hover:text-primary-500"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
          )}

          <Badge variant="info" className="text-xs flex-shrink-0">
            {permission.actions.length} actions
          </Badge>
        </div>

        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className="text-gray-400 hover:text-red-500 ml-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Description (optional) */}
          <div>
            {showDescription ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={permission.description || ''}
                  onChange={(e) => updateDescription(e.target.value)}
                  placeholder="Optional description for this permission group..."
                  disabled={disabled}
                  rows={2}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            ) : (
              !disabled && (
                <button
                  type="button"
                  onClick={() => setShowDescription(true)}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  + Add description
                </button>
              )
            )}
          </div>

          {/* Resources */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resources
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {permission.resources.map((resource) => (
                <Badge
                  key={resource}
                  variant={getResourceBadgeVariant(resource)}
                  className="flex items-center gap-1"
                >
                  <span className="font-mono text-xs">
                    {resource === '*' ? 'All Resources (*)' : resource}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeResource(resource)}
                      className="ml-1 hover:text-red-600"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>

            {/* Smart Resource Builder */}
            {!disabled && (
              <SmartResourceBuilder
                tenantId={tenantId}
                onAddResource={addResource}
                existingResources={permission.resources}
                roleType={roleType}
                disabled={disabled}
              />
            )}
          </div>

          {/* Actions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Actions
            </label>
            <div className="flex flex-wrap gap-2">
              {permission.actions.map((action) => (
                <Badge
                  key={action}
                  variant="success"
                  className="flex items-center gap-1"
                >
                  <span className="font-mono text-xs">{formatAction(action)}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => toggleAction(action)}
                      className="ml-1 hover:text-red-600"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
            {permission.resources.length === 0 && roleType === 'application' && (
              <p className="text-sm text-gray-500 italic">
                Select resources above to see available actions.
              </p>
            )}
            {!disabled && (
              <div className="mt-2">
                <select
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      toggleAction(e.target.value);
                    }
                  }}
                >
                  <option value="">+ Add action...</option>
                  {availableActions
                    .filter((a) => !permission.actions.includes(a.action))
                    .map((a) => (
                      <option key={a.action} value={a.action}>
                        {a.label} ({a.action})
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ResourceScopedPermissionBuilder: React.FC<ResourceScopedPermissionBuilderProps> = ({
  tenantId,
  permissions,
  onChange,
  roleType = 'application',
  disabled = false,
}) => {
  // Fetch applications for resource selection
  const { data: applications = [], isLoading: isLoadingApps } = useApplications(tenantId);

  // Add a new permission group
  const addPermissionGroup = () => {
    // For app_domain roles, start with empty resources (user must select application first)
    if (roleType === 'app_domain') {
      onChange([
        ...permissions,
        {
          actions: [],
          resources: [],
        },
      ]);
    } else {
      onChange([
        ...permissions,
        {
          actions: [],
          resources: [],
        },
      ]);
    }
  };

  // Update a permission group
  const updatePermissionGroup = (index: number, permission: ResourceScopedPermission) => {
    const newPermissions = [...permissions];
    newPermissions[index] = permission;
    onChange(newPermissions);
  };

  // Remove a permission group
  const removePermissionGroup = (index: number) => {
    onChange(permissions.filter((_, i) => i !== index));
  };

  // Add a quick permission preset
  const addQuickPreset = (actions: string[], resources: string[], name?: string) => {
    onChange([
      ...permissions,
      {
        actions,
        resources,
        name,
      },
    ]);
  };

  return (
    <div className="space-y-4">
      {/* Current Permission Groups */}
      {permissions.length > 0 ? (
        <div className="space-y-3">
          {permissions.map((permission, index) =>
            roleType === 'app_domain' ? (
              <AppDomainPermissionGroupEditor
                key={index}
                index={index}
                permission={permission}
                onUpdate={updatePermissionGroup}
                onRemove={removePermissionGroup}
                applications={applications}
                disabled={disabled}
              />
            ) : (
              <PermissionGroupEditor
                key={index}
                index={index}
                permission={permission}
                onUpdate={updatePermissionGroup}
                onRemove={removePermissionGroup}
                roleType={roleType}
                tenantId={tenantId}
                applications={applications}
                disabled={disabled}
              />
            )
          )}
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No permissions</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add permission groups to define what actions are allowed on which resources.
          </p>
        </div>
      )}

      {/* Add Permission Group */}
      {!disabled && (
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={addPermissionGroup}
            className="w-full"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Permission Group
          </Button>

          {/* Quick Presets - Not shown for app_domain roles (actions come from selected application) */}
          {roleType !== 'app_domain' && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Presets</h4>
              <div className="flex flex-wrap gap-2">
                {roleType === 'application' ? (
                  <>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100 border border-green-200"
                      onClick={() => addQuickPreset(['application:InvokeFunction', 'application:InvokeJob'], ['*'], 'Execute All Applications')}
                    >
                      Execute All Apps
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 border border-blue-200"
                      onClick={() =>
                        addQuickPreset(
                          ['volume:GetObject', 'volume:PutObject', 'volume:DeleteObject', 'volume:ListObjects'],
                          ['*'],
                          'Full Volume Access'
                        )
                      }
                    >
                      Full Volume Access
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-amber-50 text-amber-700 rounded-md hover:bg-amber-100 border border-amber-200"
                      onClick={() =>
                        addQuickPreset(
                          ['iam:GetUserData', 'iam:GetGroupData', 'iam:GetMembershipData', 'iam:ListUsers'],
                          ['*'],
                          'Read IAM Data'
                        )
                      }
                    >
                      Read IAM Data
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-md hover:bg-red-100 border border-red-200"
                      onClick={() => addQuickPreset(['*'], ['*'], 'Full Admin Access')}
                    >
                      Full Admin
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 border border-blue-200"
                      onClick={() =>
                        addQuickPreset(
                          [
                            'iam:user:read',
                            'iam:group:read',
                            'iam:role:read',
                            'app:application:read',
                            'data:volume:read',
                          ],
                          ['*'],
                          'Read-Only Viewer'
                        )
                      }
                    >
                      Read-Only Viewer
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100 border border-green-200"
                      onClick={() =>
                        addQuickPreset(
                          [
                            'iam:user:read',
                            'iam:user:create',
                            'iam:user:update',
                            'iam:group:read',
                          ],
                          ['*'],
                          'User Manager'
                        )
                      }
                    >
                      User Manager
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading indicator */}
      {isLoadingApps && (
        <div className="flex items-center justify-center py-2">
          <Spinner size="sm" />
          <span className="ml-2 text-sm text-gray-500">Loading resources...</span>
        </div>
      )}

      {/* Help Text */}
      <div className="text-xs text-gray-500 bg-gray-50 rounded-md p-3">
        <p className="font-medium mb-1">Permission Format:</p>
        {roleType === 'app_domain' ? (
          <>
            <p>
              App Domain roles define what users can do within specific tenant applications.
              Each permission group is tied to <strong>one application</strong>.
            </p>
            <p className="mt-1">
              <strong>Actions:</strong> Select from the application&apos;s defined actions (e.g., &quot;read&quot;, &quot;write&quot;)
            </p>
            <p className="mt-1">
              <strong>Resource:</strong> Automatically set to the application code (e.g., &quot;inventory&quot;)
            </p>
            <p className="mt-1">
              <strong>Stored as:</strong>{' '}
              <code className="bg-gray-200 px-1 rounded">applicationCode:action</code> (e.g., &quot;inventory:read&quot;)
            </p>
          </>
        ) : (
          <>
            <p>
              Each permission group contains one or more <strong>actions</strong> that apply to the
              specified <strong>resources</strong>. Use the <strong>name</strong> field to organize and identify permission groups.
            </p>
            <p className="mt-1">
              <strong>Actions:</strong> <code className="bg-gray-200 px-1 rounded">domain:resource:action</code>{' '}
              (e.g., &quot;app:application:execute&quot;, &quot;iam:user:read&quot;)
            </p>
            <p className="mt-1">
              <strong>Resources:</strong> Use the smart builder to select resource types, or use patterns like{' '}
              <code className="bg-gray-200 px-1 rounded">volume:documents</code> or{' '}
              <code className="bg-gray-200 px-1 rounded">secret:api-*</code>
            </p>
          </>
        )}
      </div>
    </div>
  );
};
